import { MouseEvent, RefObject, ReactNode } from "react";

import { AutofillIndicator } from "./autofill-components";
import {
  SectionButtons,
  SectionHeading,
  SectionForm,
  PageWrapper,
  Page,
  Section,
} from "./section-components";
import { ConnectionBanner } from "./section-components/connection-banner";
import { PreviewFormFooter } from "./shared-components/preview-form-footer";
import {
  AutoFillStatus,
  ConnectionItem,
  SectionErrors,
  SectionItem,
  TeamItem,
  Toast,
} from "../types";

export type IntakeSectionProps = {
  team: TeamItem;
  connection?: ConnectionItem;
  autoFillSection?: SectionItem;
  sections: SectionItem[];
  selected: SectionItem;
  sidebar: boolean;
  setSidebar: (sidebar: boolean) => void;
  onPreviousButton?: () => void;
  onNextButton?: (e: MouseEvent<HTMLButtonElement>) => void;
  onSubmitButton?: (e: MouseEvent<HTMLButtonElement>) => void;
  onSubmit?: () => void;
  onReset?: () => void;
  children?: ReactNode;
  errors?: SectionErrors;
  errorsRef?: RefObject<HTMLDivElement>;
  submitError?: string;
  isForm?: boolean;
  hasPayment: boolean;
  previewErrorToggle?: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  showConnectionBanner?: [
    boolean,
    React.Dispatch<React.SetStateAction<boolean>>,
  ];
  retrySubmitResponses: () => void;
  autoFillStatus?: AutoFillStatus;
  loading?: boolean;
  aiFillFromMatterInfo?: boolean;
  isPreview?: boolean;
  setToasts?: (toasts: Toast[]) => void;
  isTeamHidden?: boolean;
  isWebEnquiry?: boolean;
  isPrint?: boolean;
};

const noop = () => {};

export const IntakeSection = ({
  team,
  connection,
  autoFillSection,
  sections,
  selected,
  sidebar,
  setSidebar,
  onPreviousButton,
  onNextButton,
  onSubmitButton,
  onSubmit,
  onReset,
  children,
  submitError,
  hasPayment,
  errors,
  errorsRef,
  isForm = true,
  previewErrorToggle,
  showConnectionBanner,
  retrySubmitResponses,
  autoFillStatus,
  loading,
  aiFillFromMatterInfo,
  isPreview,
  setToasts,
  isTeamHidden,
  isWebEnquiry,
  isPrint,
}: IntakeSectionProps) => {
  const validationAlert = () => {
    if (previewErrorToggle && setToasts && errors && !previewErrorToggle[0]) {
      setToasts([
        {
          text: "Turn on 'Ignore Required Fields' to preview without validation.",
          palette: "warning",
          width: "medium",
          elaboration: {
            text: "Turn On",
            onClick: () => previewErrorToggle[1](true),
          },
        },
      ]);
    }
    return noop();
  };

  const selectedIndex = sections.findIndex((s) => s.uri === selected?.uri);

  const buttons = {
    onPrevious:
      selectedIndex > 0 || (selectedIndex === 0 && autoFillSection)
        ? onPreviousButton || validationAlert
        : undefined,
    onNext: sections.length > 1 ? onNextButton || validationAlert : undefined,
    onSubmit:
      sections.length === 1 ? onSubmitButton || validationAlert : undefined,
  };

  const isLoadingBeforeAutoFill = loading && aiFillFromMatterInfo;
  const autoFillInProgress = autoFillStatus === "filling";

  return (
    <PageWrapper sidebar={sidebar}>
      {!isWebEnquiry && (
        <ConnectionBanner
          connectionError={connection?.status === "error"}
          showConnectionBanner={showConnectionBanner}
          retry={retrySubmitResponses}
        />
      )}
      {(autoFillStatus || isLoadingBeforeAutoFill) && (
        <AutofillIndicator
          status={autoFillStatus}
          loading={isLoadingBeforeAutoFill}
        />
      )}
      <Page
        isPreview={isPreview}
        className={previewErrorToggle && "mb-[5.6rem]"}
      >
        <Section>
          <SectionForm onSubmit={onSubmit} onReset={onReset} isForm={isForm}>
            <SectionButtons
              isForm={isForm}
              team={team}
              hasError={submitError}
              hasPayment={hasPayment}
              setSidebar={setSidebar}
              {...buttons}
              disabled={{
                submit: autoFillInProgress || isLoadingBeforeAutoFill,
                back: autoFillInProgress || isLoadingBeforeAutoFill,
                next: autoFillInProgress || isLoadingBeforeAutoFill,
              }}
              showSectionsButton={!!(sections.length > 1 || autoFillSection)}
              isTeamHidden={isTeamHidden}
              setToasts={setToasts}
              isPrint={isPrint}
            >
              <SectionHeading
                section={selected}
                errors={errors}
                errorsRef={errorsRef}
                hideErrors={previewErrorToggle?.[0]}
              />
              <div className="py-[5.6rem]">{children}</div>
            </SectionButtons>
          </SectionForm>
        </Section>
      </Page>
      {previewErrorToggle && (
        <PreviewFormFooter previewErrorToggle={previewErrorToggle} />
      )}
    </PageWrapper>
  );
};
